import { useEffect, useId, useRef, useState } from 'react';
import { api } from '../../api';
import { Seletor } from '../../Filtros';
import { hojeIso, nomeDoContato, numero } from '../../formato';
import { degrauDe, ESTAGIO_DA_OPORTUNIDADE } from '../../situacoes';
import { Area, Campo, CampoNumerico, Dialogo, ErroDaApi, Escolha, ZonaDeArquivo } from '../../ui';
import { faltaPara, Rodape } from './JanelasDaConversa';
import {
  CANAL_DE_ENVIO, ESTAGIOS_DO_QUADRO, LINHA, MOTIVO_DA_PERDA, PAPEL, TIPO_DE_TAREFA,
  type DetalheDaOportunidade, type Pessoa,
} from './crm';

/**
 * As janelas do CRM do Comercial — onda 550 (Plano 2 · Comercial, Etapa 1,
 * Tarefa 15). Tudo o que pede alguma coisa à pessoa abre no `Dialogo` da
 * casa (P-JAN-01), com o rodapé da casa: o secundário à esquerda e a ação à
 * direita; o «Gravar» que ainda não pode gravar fica à vista, indisponível
 * (`aria-disabled`), com a frase do que falta logo acima (P-UI-25). Todo
 * motivo tem 10 caracteres ou mais (P-TXT-10).
 *
 *  · Mover para… — o estágio e o motivo, que fica no histórico (RN-471 ·
 *    RN-C02); a perdida pede também o motivo da perda (RN-472 · RN-C03);
 *  · Registrar proposta — a feita em Word, com o PDF (RN-480 · RN-C11);
 *  · Mudar a oportunidade — os dados, a próxima ação, o acompanhamento e o
 *    responsável;
 *  · Nova oportunidade — a criada à mão (I17), com um contato que já existe
 *    ou um novo;
 *  · Nova tarefa e Cancelar a tarefa (o motivo);
 *  · Ligar a um cliente — o contato de vários clientes, com o papel.
 */

const MINIMO = 10;

/**
 * O que o campo do motivo diz embaixo: enquanto é curto, o mínimo e quanto
 * falta («Escreva pelo menos 10 caracteres: faltam 3.», T-FUN.11 da Tarefa
 * 18); com 10 ou mais, para onde o motivo vai.
 */
function contagem(t: string) {
  const n = MINIMO - t.trim().length;
  return n > 0 ? `Escreva pelo menos ${MINIMO} caracteres: ${n === 1 ? 'falta 1' : `faltam ${n}`}.`
    : 'Fica no histórico da oportunidade.';
}

/** A data e hora local do `<input type="datetime-local">` como ISO, ou nulo. */
const isoDoCampo = (v: string) => (v ? new Date(v).toISOString() : null);
/** O instante ISO no formato do `<input type="datetime-local">` (hora local). */
function campoDoIso(iso: string | null | undefined) {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const OPCOES_DE_LINHA = Object.entries(LINHA).map(([valor, texto]) => ({ valor, texto }));

/** Quem pode ficar com a oportunidade (a lista vem uma vez por janela). */
function useResponsaveis(aberta: boolean) {
  const [r, setR] = useState<Pessoa[]>([]);
  useEffect(() => {
    if (!aberta) return;
    api<Pessoa[]>('/comercial/oportunidades/responsaveis').then(setR).catch(() => setR([]));
  }, [aberta]);
  return r;
}

// ── Mover para… ──────────────────────────────────────────────────────

export function JanelaMover({ aberta, oportunidade, para: paraInicial = null, aoEscolherPara, aoFechar, aoMover }: {
  aberta: boolean;
  oportunidade: { id: string; estagio: string; contato: string };
  /** O estágio já escolhido — o da coluna em que o cartão caiu, no quadro do funil (Tarefa 18). */
  para?: string | null;
  /** Avisa cada escolha de estágio (o quadro do funil põe o cartão que espera na coluna escolhida). */
  aoEscolherPara?(para: string | null): void;
  aoFechar(): void;
  /** Gravado: diz para que estágio o cartão foi. */
  aoMover(para: string): void;
}) {
  const [para, setParaInterno] = useState<string | null>(null);
  const setPara = (v: string | null) => { setParaInterno(v); aoEscolherPara?.(v); };
  const [motivo, setMotivo] = useState('');
  const [perda, setPerda] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!aberta) return;
    setParaInterno(paraInicial); setMotivo(''); setPerda(null); setErro(null);
  }, [aberta]); // eslint-disable-line react-hooks/exhaustive-deps

  const destinos = [...ESTAGIOS_DO_QUADRO, 'descartada'].filter((e) => e !== oportunidade.estagio);
  const falta = faltaPara('gravar', [
    ...(!para ? ['o estágio'] : []),
    ...(para === 'perdida' && !perda ? ['o motivo da perda'] : []),
    ...(motivo.trim().length < MINIMO ? ['o motivo (10 caracteres ou mais)'] : []),
  ]);

  const gravar = async () => {
    setOcupado(true);
    setErro(null);
    try {
      await api(`/comercial/oportunidades/${oportunidade.id}/mover`, {
        metodo: 'POST',
        corpo: { para, motivo: motivo.trim(), ...(para === 'perdida' ? { motivoPerda: perda } : {}) },
      });
      aoMover(para!);
    } catch (e) {
      setErro(e);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Dialogo aberto={aberta} titulo="Mover para…" rotulo={oportunidade.contato} aoFechar={aoFechar}>
      <div className="janela-mover">
        <Escolha rotulo="Para o estágio" valor={para} aoEscolher={setPara} classe="escolha-estagios"
          opcoes={destinos.map((e) => ({
            valor: e,
            texto: e === 'descartada' ? 'Descartada (não é pedido)' : degrauDe(ESTAGIO_DA_OPORTUNIDADE, e).texto,
          }))} />
        {para === 'perdida' && (
          <Escolha rotulo="O motivo da perda" valor={perda} aoEscolher={setPerda}
            opcoes={Object.entries(MOTIVO_DA_PERDA).map(([valor, texto]) => ({ valor, texto }))}
            dica="Os motivos de perda alimentam o painel do Comercial." />
        )}
        <Area rotulo="Por que o cartão vai para este estágio" rows={3} value={motivo}
          onChange={(e) => setMotivo(e.target.value)} dica={contagem(motivo)} />
        <ErroDaApi erro={erro} />
      </div>
      {/* O rodapé é o último filho do corpo da janela: preso embaixo, na faixa da casa (P-JAN-03/04). */}
      <Rodape acao="Gravar" falta={falta} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void gravar()} />
    </Dialogo>
  );
}

// ── Registrar proposta (a feita em Word) ─────────────────────────────

export function JanelaProposta({ aberta, oportunidade, aoFechar, aoGravar }: {
  aberta: boolean;
  oportunidade: { id: string; contato: string };
  aoFechar(): void;
  aoGravar(): void;
}) {
  const [pdf, setPdf] = useState<File | null>(null);
  const [numeroDaProposta, setNumero] = useState('');
  const [revisao, setRevisao] = useState('0');
  const [valor, setValor] = useState('');
  const [enviadaEm, setEnviadaEm] = useState(hojeIso());
  const [canal, setCanal] = useState<string | null>(null);
  const [validade, setValidade] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!aberta) return;
    setPdf(null); setNumero(''); setRevisao('0'); setValor(''); setEnviadaEm(hojeIso()); setCanal(null);
    setValidade(''); setErro(null);
  }, [aberta]);

  const numeroOk = /^P-\d{8}$/.test(numeroDaProposta.trim().toUpperCase());
  const falta = faltaPara('gravar', [
    ...(!pdf ? ['o PDF'] : []),
    ...(!numeroOk ? ['o número (P- e oito algarismos)'] : []),
    ...(!/^\d{1,3}$/.test(revisao.trim()) ? ['a revisão'] : []),
    ...(!/\d/.test(valor) ? ['o valor'] : []),
    ...(!enviadaEm ? ['a data do envio'] : []),
    ...(!canal ? ['por onde foi'] : []),
  ]);

  const gravar = async () => {
    setOcupado(true);
    setErro(null);
    try {
      const f = new FormData();
      f.append('pdf', pdf!);
      f.append('numero', numeroDaProposta.trim().toUpperCase());
      f.append('revisao', revisao.trim());
      f.append('valor', valor.trim());
      f.append('enviadaEm', enviadaEm);
      f.append('canalEnvio', canal!);
      if (validade) f.append('validade', validade);
      await api(`/comercial/oportunidades/${oportunidade.id}/propostas`, { metodo: 'POST', formulario: f });
      aoGravar();
    } catch (e) {
      setErro(e);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Dialogo aberto={aberta} titulo="Registrar proposta" rotulo={oportunidade.contato} aoFechar={aoFechar}>
      <div className="janela-proposta">
        <p className="janela-explica">
          A proposta feita em Word, já enviada ao cliente. O PDF fica guardado com a oportunidade, e a
          proposta registrada não muda: a correção é uma revisão nova.
        </p>
        <ZonaDeArquivo rotulo="O PDF que foi ao cliente" aceita="application/pdf,.pdf" tipos="PDF, até 25 MB"
          arquivos={pdf} aoEscolher={(l) => setPdf(l[0] ?? null)} />
        <div className="janela-linha">
          <Campo rotulo="Número" value={numeroDaProposta} placeholder="P-26101639" autoComplete="off"
            onChange={(e) => setNumero(e.target.value)} dica="P- e oito algarismos, como no Word." />
          <CampoNumerico rotulo="Revisão" inteiro value={revisao} onChange={(e) => setRevisao(e.target.value)}
            dica="0 para a R00, 1 para a R01." />
        </div>
        <div className="janela-linha">
          <CampoNumerico rotulo="Valor (R$)" value={valor} placeholder="12.850,00"
            onChange={(e) => setValor(e.target.value)} />
          <Campo rotulo="Enviada em" type="date" value={enviadaEm} max={hojeIso()}
            onChange={(e) => setEnviadaEm(e.target.value)} />
        </div>
        <Escolha rotulo="Por onde foi" valor={canal} aoEscolher={setCanal}
          opcoes={Object.entries(CANAL_DE_ENVIO).map(([v, texto]) => ({ valor: v, texto }))} />
        <Campo rotulo="Válida até" type="date" value={validade} min={enviadaEm}
          onChange={(e) => setValidade(e.target.value)}
          dica="Sem data, vale 15 dias contados do envio." />
        <ErroDaApi erro={erro} />
        <Rodape acao="Gravar" falta={falta} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void gravar()} />
      </div>
    </Dialogo>
  );
}

// ── Mudar a oportunidade ─────────────────────────────────────────────

export function JanelaDaOportunidade({ aberta, d, aoFechar, aoGravar }: {
  aberta: boolean;
  d: DetalheDaOportunidade;
  aoFechar(): void;
  aoGravar(): void;
}) {
  const o = d.oportunidade;
  const responsaveis = useResponsaveis(aberta);
  const [linha, setLinha] = useState<string | null>(o.linha);
  const [endereco, setEndereco] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [uf, setUf] = useState('');
  const [escopo, setEscopo] = useState('');
  const [furos, setFuros] = useState('');
  const [valor, setValor] = useState('');
  const [acao, setAcao] = useState('');
  const [acaoEm, setAcaoEm] = useState('');
  const [pessoal, setPessoal] = useState<string | null>('agente');
  const [responsavel, setResponsavel] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!aberta) return;
    setLinha(o.linha); setEndereco(o.local.endereco ?? ''); setMunicipio(o.local.municipio ?? '');
    setUf(o.local.uf ?? ''); setEscopo(o.escopo ?? ''); setFuros(o.furos ? String(o.furos) : '');
    setValor(o.valorEstimado != null && !o.valorPelaRegra ? numero(o.valorEstimado, 2) : '');
    setAcao(o.proximaAcao ?? ''); setAcaoEm(campoDoIso(o.proximaAcaoEm));
    setPessoal(o.acompanhamentoPessoal ? 'pessoal' : 'agente'); setResponsavel(o.responsavel?.id ?? null);
    setErro(null);
  }, [aberta]); // eslint-disable-line react-hooks/exhaustive-deps

  const furosOk = furos.trim() === '' || /^\d{1,5}$/.test(furos.trim());
  const valorNumero = valor.trim() === '' ? null : Number(valor.replace(/\./g, '').replace(',', '.'));
  const falta = faltaPara('gravar', [
    ...(!furosOk ? ['os furos em número inteiro'] : []),
    ...(valorNumero !== null && !Number.isFinite(valorNumero) ? ['o valor em reais'] : []),
    ...(acaoEm && !acao.trim() ? ['o que é a próxima ação'] : []),
    ...(uf.trim() && !/^[A-Za-z]{2}$/.test(uf.trim()) ? ['a UF com duas letras'] : []),
  ]);

  const gravar = async () => {
    setOcupado(true);
    setErro(null);
    try {
      await api(`/comercial/oportunidades/${o.id}`, {
        metodo: 'PATCH',
        corpo: {
          linha: linha ?? null, endereco: endereco.trim() || null, municipio: municipio.trim() || null,
          uf: uf.trim().toUpperCase() || null, escopo: escopo.trim() || null,
          furos: furos.trim() ? Number(furos) : null,
          ...(o.veValor ? { valorEstimado: valorNumero } : {}),
          proximaAcao: acao.trim() || null, proximaAcaoEm: isoDoCampo(acaoEm),
          acompanhamentoPessoal: pessoal === 'pessoal',
          ...(responsavel && responsavel !== o.responsavel?.id ? { responsavelUsuarioId: responsavel } : {}),
        },
      });
      aoGravar();
    } catch (e) {
      setErro(e);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Dialogo aberto={aberta} titulo="Mudar a oportunidade" rotulo={nomeDoContato(o.contato.nome)} aoFechar={aoFechar}>
      <div className="janela-oportunidade">
        <Escolha rotulo="Linha de serviço" valor={linha} aoEscolher={setLinha} opcoes={OPCOES_DE_LINHA} />
        <Campo rotulo="Endereço da obra" value={endereco} onChange={(e) => setEndereco(e.target.value)} />
        <div className="janela-linha">
          <Campo rotulo="Município" value={municipio} onChange={(e) => setMunicipio(e.target.value)} />
          <Campo rotulo="UF" value={uf} maxLength={2} onChange={(e) => setUf(e.target.value)} />
        </div>
        <Area rotulo="Escopo" rows={3} value={escopo} onChange={(e) => setEscopo(e.target.value)} />
        <div className="janela-linha">
          <CampoNumerico rotulo="Furos" inteiro value={furos} onChange={(e) => setFuros(e.target.value)} />
          {o.veValor && (
            <CampoNumerico rotulo="Valor estimado (R$)" value={valor} onChange={(e) => setValor(e.target.value)}
              dica="Vazio, vale a estimativa pela regra simples." />
          )}
        </div>
        <div className="janela-linha">
          <Campo rotulo="Próxima ação" value={acao} onChange={(e) => setAcao(e.target.value)}
            placeholder="Ligar para saber da aprovação" />
          <Campo rotulo="Quando" type="datetime-local" value={acaoEm} onChange={(e) => setAcaoEm(e.target.value)} />
        </div>
        <Escolha rotulo="O acompanhamento" valor={pessoal} limpavel={false} aoEscolher={(v) => setPessoal(v ?? 'agente')}
          opcoes={[{ valor: 'agente', texto: 'Automático' }, { valor: 'pessoal', texto: 'Eu acompanho pessoalmente' }]}
          dica="Pessoal: o agente não manda os acompanhamentos desta proposta." />
        <Seletor rotulo="Responsável" valor={responsavel} todos="Escolha quem cuida"
          opcoes={responsaveis.map((p) => ({ valor: p.id, texto: p.nome }))} aoMudar={setResponsavel} />
        <ErroDaApi erro={erro} />
        <Rodape acao="Gravar" falta={falta} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void gravar()} />
      </div>
    </Dialogo>
  );
}

// ── O cliente, achado enquanto se digita ─────────────────────────────

interface ClienteDaLista { id: string; razao_social: string; nome_fantasia: string | null }
const nomeDoCliente = (c: ClienteDaLista) => c.nome_fantasia || c.razao_social;

/**
 * O cliente achado enquanto se digita — no molde da `EscolhaPorCodigo`
 * (`ui.tsx`): o campo é um combobox, a lista abre embaixo, ↑ ↓ marcam,
 * Enter escolhe, Esc fecha. Busca na API (`GET /clientes?busca=…&ativos=1`),
 * e só entre os ativos: a lista inteira em ordem alfabética cortava nos 200
 * primeiros e escondia o resto (decisão do coordenador, revisão da Tarefa 15).
 */
export function EscolhaDeCliente({ valor, aoEscolher, dica }: {
  valor: ClienteDaLista | null;
  aoEscolher(c: ClienteDaLista | null): void;
  dica?: string;
}) {
  const id = useId();
  const [texto, setTexto] = useState('');
  const [aberto, setAberto] = useState(false);
  const [marcado, setMarcado] = useState(0);
  const [achados, setAchados] = useState<ClienteDaLista[] | null>(null);
  const caixa = useRef<HTMLDivElement>(null);
  const termo = texto.trim();

  useEffect(() => {
    if (termo.length < 2) { setAchados(null); return undefined; }
    let vale = true;
    const t = window.setTimeout(() => {
      api<ClienteDaLista[]>(`/clientes?busca=${encodeURIComponent(termo)}&ativos=1`)
        .then((l) => { if (vale) { setAchados(l.slice(0, 30)); setMarcado(0); } })
        .catch(() => { if (vale) setAchados([]); });
    }, 250);
    return () => { vale = false; window.clearTimeout(t); };
  }, [termo]);

  /* Clique fora fecha — e não escolhe. */
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) { setAberto(false); setTexto(''); }
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const lista = achados ?? [];
  const pegar = (c: ClienteDaLista) => { aoEscolher(c); setTexto(''); setAberto(false); };
  const aoTeclar = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setAberto(true);
      setMarcado((m) => {
        const n = lista.length;
        if (!n) return 0;
        return e.key === 'ArrowDown' ? (m + 1) % n : (m - 1 + n) % n;
      });
      return;
    }
    if (e.key === 'Enter') {
      if (aberto && lista[marcado]) { e.preventDefault(); pegar(lista[marcado]); }
      return;
    }
    if (e.key === 'Escape') { setAberto(false); setTexto(''); }
  };
  const listaId = `${id}-lista`;

  return (
    <div className="campo escolha-codigo largo escolha-de-cliente" ref={caixa}>
      <label className="rotulo" htmlFor={id}>Cliente</label>
      <div className="campo-codigo">
        <input id={id} type="text" role="combobox" aria-expanded={aberto} aria-controls={listaId}
          aria-autocomplete="list" autoComplete="off"
          value={texto !== '' ? texto : (valor ? nomeDoCliente(valor) : '')}
          placeholder={valor ? undefined : 'digite parte do nome…'}
          onFocus={() => setAberto(true)} onKeyDown={aoTeclar}
          onChange={(e) => { setTexto(e.currentTarget.value); setAberto(true); }} />
        {valor && texto === '' && (
          <button type="button" className="botao secundario" onClick={() => aoEscolher(null)}>Tirar o cliente</button>
        )}
      </div>
      {aberto && (
        <ul className="lista-codigos" id={listaId} role="listbox">
          {termo.length < 2 ? (
            <li className="nada" role="presentation">Digite duas letras ou mais do nome do cliente.</li>
          ) : achados === null ? (
            <li className="nada" role="presentation">Procurando…</li>
          ) : lista.length === 0 ? (
            <li className="nada" role="presentation">Nenhum cliente ativo com «{termo}».</li>
          ) : lista.map((c, i) => (
            <li key={c.id} role="option" aria-selected={valor?.id === c.id}
              className={i === marcado ? 'marcado' : undefined}
              onMouseEnter={() => setMarcado(i)}
              onMouseDown={(e) => { e.preventDefault(); pegar(c); }}
              onClick={() => pegar(c)}>
              <b>{nomeDoCliente(c)}</b>
              {c.nome_fantasia && <span className="det">{c.razao_social}</span>}
            </li>
          ))}
        </ul>
      )}
      {dica && <span className="dica">{dica}</span>}
    </div>
  );
}

// ── Nova oportunidade (I17: criada à mão) ────────────────────────────

interface ContatoAchado { id: string; nome: string; cargo: string | null; clientes: { nome: string }[] }

export function JanelaNovaOportunidade({ aberta, aoFechar, aoCriar }: {
  aberta: boolean;
  aoFechar(): void;
  aoCriar(id: string): void;
}) {
  const [qual, setQual] = useState<string | null>('existente');
  const [busca, setBusca] = useState('');
  const [achados, setAchados] = useState<ContatoAchado[]>([]);
  const [contato, setContato] = useState<ContatoAchado | null>(null);
  const [nome, setNome] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [cliente, setCliente] = useState<ClienteDaLista | null>(null);
  const [linha, setLinha] = useState<string | null>(null);
  const [municipio, setMunicipio] = useState('');
  const [uf, setUf] = useState('SC');
  const [escopo, setEscopo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!aberta) return;
    setQual('existente'); setBusca(''); setAchados([]); setContato(null); setNome(''); setWhatsapp(''); setEmail('');
    setCliente(null); setLinha(null); setMunicipio(''); setUf('SC'); setEscopo(''); setErro(null);
  }, [aberta]);

  useEffect(() => {
    if (!aberta || qual !== 'existente' || busca.trim().length < 2) { setAchados([]); return undefined; }
    const t = window.setTimeout(() => {
      api<ContatoAchado[]>(`/comercial/contatos?busca=${encodeURIComponent(busca.trim())}`)
        .then((l) => setAchados(l.slice(0, 8))).catch(() => setAchados([]));
    }, 250);
    return () => window.clearTimeout(t);
  }, [busca, qual, aberta]);

  const falta = faltaPara('gravar', [
    ...(qual === 'existente' && !contato ? ['o contato (busque e escolha)'] : []),
    ...(qual === 'novo' && nome.trim().length < 2 ? ['o nome do contato'] : []),
  ]);

  const gravar = async () => {
    setOcupado(true);
    setErro(null);
    try {
      const r = await api<{ id: string }>('/comercial/oportunidades', {
        metodo: 'POST',
        corpo: {
          ...(qual === 'existente' ? { contatoId: contato!.id } : {
            novoContato: {
              nome: nome.trim(),
              ...(whatsapp.trim() ? { whatsapp: whatsapp.trim() } : {}),
              ...(email.trim() ? { email: email.trim() } : {}),
              ...(cliente ? { clienteId: cliente.id } : {}),
            },
          }),
          ...(linha ? { linha } : {}),
          ...(municipio.trim() ? { municipio: municipio.trim() } : {}),
          ...(uf.trim() ? { uf: uf.trim().toUpperCase() } : {}),
          ...(escopo.trim() ? { escopo: escopo.trim() } : {}),
        },
      });
      aoCriar(r.id);
    } catch (e) {
      setErro(e);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Dialogo aberto={aberta} titulo="Nova oportunidade" aoFechar={aoFechar}>
      <div className="janela-nova-oportunidade">
        <p className="janela-explica">
          O pedido que chegou fora da caixa de entrada — por uma ligação no celular, numa visita, numa
          indicação. Fica com você, e nasce em Novo.
        </p>
        <Escolha rotulo="O contato" valor={qual} limpavel={false} aoEscolher={(v) => setQual(v ?? 'existente')}
          opcoes={[{ valor: 'existente', texto: 'Um contato que já existe' }, { valor: 'novo', texto: 'Um contato novo' }]} />
        {qual === 'existente' ? (
          <>
            <Campo rotulo="Buscar o contato" type="search" value={busca} autoComplete="off"
              onChange={(e) => { setBusca(e.target.value); setContato(null); }} placeholder="Nome, e-mail ou WhatsApp" />
            {contato ? (
              <p className="janela-escolhido">
                <strong>{nomeDoContato(contato.nome)}</strong>
                {contato.clientes[0] && <span className="painel-aux"> · {contato.clientes[0].nome}</span>}
                <button type="button" className="botao discreto" onClick={() => setContato(null)}>Trocar</button>
              </p>
            ) : achados.length > 0 && (
              <ul className="janela-achados" aria-label="Contatos achados">
                {achados.map((c) => (
                  <li key={c.id}>
                    <button type="button" className="janela-achado" onClick={() => setContato(c)}>
                      <span>{nomeDoContato(c.nome)}</span>
                      <span className="painel-aux">{[c.cargo, c.clientes[0]?.nome].filter(Boolean).join(' · ')}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            <Campo rotulo="Nome" value={nome} onChange={(e) => setNome(e.target.value)} />
            <div className="janela-linha">
              <Campo rotulo="WhatsApp" type="tel" inputMode="tel" value={whatsapp} placeholder="(47) 99999-1234"
                onChange={(e) => setWhatsapp(e.target.value)} />
              <Campo rotulo="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <EscolhaDeCliente valor={cliente} aoEscolher={setCliente}
              dica="Se ainda não é cliente, deixe em branco: o lead é a oportunidade sem cliente." />
          </>
        )}
        <Escolha rotulo="Linha de serviço" valor={linha} aoEscolher={setLinha} opcoes={OPCOES_DE_LINHA} />
        <div className="janela-linha">
          <Campo rotulo="Município da obra" value={municipio} onChange={(e) => setMunicipio(e.target.value)} />
          <Campo rotulo="UF" value={uf} maxLength={2} onChange={(e) => setUf(e.target.value)} />
        </div>
        <Area rotulo="Escopo" rows={3} value={escopo} onChange={(e) => setEscopo(e.target.value)}
          dica="O que o cliente pediu, em poucas linhas." />
        <ErroDaApi erro={erro} />
        <Rodape acao="Gravar" falta={falta} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void gravar()} />
      </div>
    </Dialogo>
  );
}

// ── As tarefas ───────────────────────────────────────────────────────

export function JanelaDaTarefa({ aberta, oportunidade, contato, aoFechar, aoGravar }: {
  aberta: boolean;
  oportunidade: string;
  contato: string;
  aoFechar(): void;
  aoGravar(): void;
}) {
  const responsaveis = useResponsaveis(aberta);
  const [tipo, setTipo] = useState<string | null>('retorno');
  const [titulo, setTitulo] = useState('');
  const [vence, setVence] = useState('');
  const [responsavel, setResponsavel] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!aberta) return;
    setTipo('retorno'); setTitulo(''); setVence(''); setResponsavel(null); setErro(null);
  }, [aberta]);

  const falta = faltaPara('gravar', [
    ...(!tipo ? ['o tipo'] : []),
    ...(titulo.trim().length < 3 ? ['o que fazer'] : []),
    ...(!vence ? ['quando vence'] : []),
  ]);

  const gravar = async () => {
    setOcupado(true);
    setErro(null);
    try {
      await api('/comercial/tarefas', {
        metodo: 'POST',
        corpo: { oportunidadeId: oportunidade, tipo, titulo: titulo.trim(), venceEm: isoDoCampo(vence),
          ...(responsavel ? { responsavelUsuarioId: responsavel } : {}) },
      });
      aoGravar();
    } catch (e) {
      setErro(e);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Dialogo aberto={aberta} titulo="Nova tarefa" rotulo={contato} aoFechar={aoFechar}>
      <div className="janela-tarefa">
        <Escolha rotulo="Tipo" valor={tipo} aoEscolher={setTipo}
          opcoes={['retorno', 'ligacao', 'acompanhamento', 'outro'].map((v) => ({ valor: v, texto: TIPO_DE_TAREFA[v] }))} />
        <Campo rotulo="O que fazer" value={titulo} onChange={(e) => setTitulo(e.target.value)}
          placeholder="Ligar para confirmar o endereço da obra" />
        <div className="janela-linha">
          <Campo rotulo="Vence em" type="datetime-local" value={vence} onChange={(e) => setVence(e.target.value)} />
          <Seletor rotulo="De quem" valor={responsavel} todos="Minha"
            opcoes={responsaveis.map((p) => ({ valor: p.id, texto: p.nome }))} aoMudar={setResponsavel} />
        </div>
        <ErroDaApi erro={erro} />
        <Rodape acao="Gravar" falta={falta} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void gravar()} />
      </div>
    </Dialogo>
  );
}

export function JanelaCancelarTarefa({ aberta, tarefa, aoFechar, aoGravar }: {
  aberta: boolean;
  tarefa: { id: string; titulo: string } | null;
  aoFechar(): void;
  aoGravar(): void;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => { if (aberta) { setMotivo(''); setErro(null); } }, [aberta]);
  const falta = faltaPara('gravar', motivo.trim().length < MINIMO ? ['o motivo (10 caracteres ou mais)'] : []);
  const gravar = async () => {
    setOcupado(true);
    setErro(null);
    try {
      await api(`/comercial/tarefas/${tarefa!.id}`, { metodo: 'PATCH', corpo: { situacao: 'cancelada', motivo: motivo.trim() } });
      aoGravar();
    } catch (e) {
      setErro(e);
    } finally {
      setOcupado(false);
    }
  };
  return (
    <Dialogo aberto={aberta} titulo="Cancelar a tarefa" rotulo={tarefa?.titulo} aoFechar={aoFechar}>
      <div className="janela-cancelar">
        <Area rotulo="Por que a tarefa não vai ser feita" rows={3} value={motivo}
          onChange={(e) => setMotivo(e.target.value)} dica={contagem(motivo).replace('da oportunidade', 'da tarefa')} />
        <ErroDaApi erro={erro} />
        <Rodape acao="Gravar" falta={falta} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void gravar()} />
      </div>
    </Dialogo>
  );
}

// ── Ligar o contato a um cliente ─────────────────────────────────────

export function JanelaLigarCliente({ aberta, contato, aoFechar, aoGravar }: {
  aberta: boolean;
  contato: { id: string; nome: string };
  aoFechar(): void;
  aoGravar(): void;
}) {
  const [cliente, setCliente] = useState<ClienteDaLista | null>(null);
  const [papel, setPapel] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => {
    if (!aberta) return;
    setCliente(null); setPapel(null); setErro(null);
  }, [aberta]);
  const falta = faltaPara('gravar', [...(!cliente ? ['o cliente'] : []), ...(!papel ? ['o papel'] : [])]);
  const gravar = async () => {
    setOcupado(true);
    setErro(null);
    try {
      await api(`/comercial/contatos/${contato.id}/clientes`, { metodo: 'POST', corpo: { clienteId: cliente!.id, papel } });
      aoGravar();
    } catch (e) {
      setErro(e);
    } finally {
      setOcupado(false);
    }
  };
  return (
    <Dialogo aberto={aberta} titulo="Ligar a um cliente" rotulo={nomeDoContato(contato.nome)} aoFechar={aoFechar}>
      <div className="janela-ligar-cliente">
        <p className="janela-explica">
          O mesmo contato pode ser de vários clientes — o projetista de uma construtora, o consultor de outra.
          Se ele já é do cliente, muda o papel.
        </p>
        <EscolhaDeCliente valor={cliente} aoEscolher={setCliente} />
        <Escolha rotulo="O papel dele neste cliente" valor={papel} aoEscolher={setPapel}
          opcoes={Object.entries(PAPEL).map(([valor, texto]) => ({ valor, texto }))} />
        <ErroDaApi erro={erro} />
        <Rodape acao="Gravar" falta={falta} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void gravar()} />
      </div>
    </Dialogo>
  );
}
